import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import {
  CSVExporterService,
  extendSortingDataAccessor,
  ObservableDataSource,
  timestampSortingAccessor,
} from '@principle-theorem/ng-shared';
import { type LabJobIncomeTimestampField } from '@principle-theorem/principle-core/interfaces';
import {
  type IPractitionerGrouping,
  type IPractitionerLabFeeGrouping,
  type IPractitionerReportRecord,
  PRACTITIONER_INCOME_TOOLTIPS,
  PractitionerLabFeeSummary,
} from '@principle-theorem/reporting';
import {
  DAY_MONTH_YEAR_FORMAT,
  type ITimePeriod,
  snapshot,
  toMoment,
  toTimePeriod,
} from '@principle-theorem/shared';
import { type Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { InvoiceIssuedInspectorToCSV } from '../../../practitioner-reporting/practitioner-income/practitioner-income-inspector/invoice-issued-inspector-to-csv';
import { InvoicePaidInspectorToCSV } from '../../../practitioner-reporting/practitioner-income/practitioner-income-inspector/invoice-paid-inspector-to-csv';
import { getColumnsForReportType } from '../practitioner-invoice-display-columns';
import { PractitionerIncomeReportFacade } from '../store/practitioner-income.facade';
import { PractitionerIncomeReportType } from '../store/practitioner-income.reducers';

@Component({
  selector: 'pr-practitioner-income-inspector',
  templateUrl: './practitioner-income-inspector.component.html',
  styleUrls: ['./practitioner-income-inspector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PractitionerIncomeInspectorComponent implements OnDestroy {
  tooltips = PRACTITIONER_INCOME_TOOLTIPS;
  result$ = new ReplaySubject<IPractitionerGrouping>(1);
  dataSource: ObservableDataSource<IPractitionerReportRecord>;
  dateFormat = DAY_MONTH_YEAR_FORMAT;
  displayedColumns$: Observable<string[]>;
  labFees$: Observable<IPractitionerLabFeeGrouping>;
  dateRange$: Observable<ITimePeriod | undefined>;
  labJobTimestampField$: Observable<LabJobIncomeTimestampField | undefined>;

  @Input()
  set result(result: IPractitionerGrouping) {
    if (result) {
      this.result$.next(result);
    }
  }

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    public reportFacade: PractitionerIncomeReportFacade,
    private _csvExporter: CSVExporterService
  ) {
    this.dataSource = new ObservableDataSource(
      this.result$.pipe(map((result) => result.records))
    );
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );
    this.displayedColumns$ = reportFacade.reportType$.pipe(
      map((reportType) => [
        reportType === PractitionerIncomeReportType.ByInvoiceIssuedDate
          ? 'issuedAt'
          : 'paidAt',
        'status',
        'invoice',
        'patient',
        ...getColumnsForReportType(reportType).filter(
          (column) => column !== 'labFeesAmount'
        ),
      ])
    );
    this.labFees$ = this.result$.pipe(
      map((result) => ({
        records: result.labFeeRecords,
        total: PractitionerLabFeeSummary.reduce(result.labFeeRecords),
      }))
    );
    this.dateRange$ = reportFacade.query$.pipe(
      map((query) =>
        query ? toTimePeriod(query.startDate, query.endDate) : undefined
      )
    );
    this.labJobTimestampField$ = reportFacade.query$.pipe(
      map((query) => query?.labJobTimestampField)
    );
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  async downloadCSV(): Promise<void> {
    const query = await snapshot(this.reportFacade.query$);
    const reportType = await snapshot(this.reportFacade.reportType$);
    if (
      !query ||
      reportType === PractitionerIncomeReportType.ByTransactionDate
    ) {
      return;
    }
    const startDate = toMoment(query.startDate).format(DAY_MONTH_YEAR_FORMAT);
    const endDate = toMoment(query.endDate).format(DAY_MONTH_YEAR_FORMAT);
    const sortedRecords = await snapshot(this.dataSource.filteredData$);

    if (reportType === PractitionerIncomeReportType.ByInvoiceIssuedDate) {
      return this._csvExporter.download(
        `invoice-issued-drilldown-${startDate}-${endDate}`,
        sortedRecords,
        new InvoiceIssuedInspectorToCSV()
      );
    }
    return this._csvExporter.download(
      `invoice-paid-drilldown-${startDate}-${endDate}`,
      sortedRecords,
      new InvoicePaidInspectorToCSV()
    );
  }

  private _sortingDataAccessor(
    data: IPractitionerReportRecord,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'issuedAt':
        return data.invoice.issuedAt
          ? timestampSortingAccessor(data.invoice.issuedAt)
          : undefined;
      case 'paidAt':
        return data.invoice.paidAt
          ? timestampSortingAccessor(data.invoice.paidAt)
          : undefined;
      case 'patient':
        return data.patient.name;
      case 'invoice':
        return data.invoice.reference;
      case 'status':
        return data.invoice.status;
      case 'depositsTakenAmount':
        return data.summary.depositsTakenAmount;
      case 'creditsAppliedAmount':
        return data.summary.creditsAppliedAmount;
      case 'discountsAppliedAmount':
        return data.summary.discountsAppliedAmount;
      case 'treatmentsInvoicedAmount':
        return data.summary.treatmentsInvoicedAmount;
      case 'invoicedCommissionAmount':
        return data.summary.invoicedCommissionAmount;
      case 'receiptedCommissionAmount':
        return data.summary.receiptedCommissionAmount;
      default:
        return;
    }
  }
}
